import React, { useCallback, useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getGladeCourseProgress,
  updateGladeCourseProgress,
} from "../../api/server";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";

const FIRST_COURSE_LESSON_COUNT = 14;

const CLASS_FIVE_SUBCLASSES = [
  { number: 1, label: "Budget Analysis" },
  { number: 2, label: "About Your Household" },
  { number: 3, label: "Monthly Income" },
  { number: 4, label: "Monthly Expenses" },
  { number: 5, label: "Debts" },
  { number: 6, label: "Legal Actions and Collections" },
  { number: 7, label: "Assets" },
  { number: 8, label: "Review and Confirm" },
  { number: 9, label: "Report" },
];

const courseNames = {
  CC: "Credit Counseling",
  DE: "Debtor Education",
};

const isChecked = (value) =>
  ["checked", "complete", "completed", "true", "1"].includes(
    String(value || "").trim().toLowerCase()
  );

const calculatePercentage = (record, lessonCount) => {
  if (!record || !lessonCount) return 0;
  const completed = Array.from({ length: lessonCount }, (_, index) =>
    isChecked(record.lessons?.[index + 1])
  ).filter(Boolean).length;
  return Math.floor((completed / lessonCount) * 100);
};

const ProgressCheckbox = ({ id, checked, label, onChange }) => (
  <div className="iagree_radio course-progress-checkbox">
    <input
      type="checkbox"
      checked={checked}
      id={id}
      aria-label={label}
      onChange={(event) => onChange(event.target.checked)}
    />
    <label htmlFor={id}> </label>
  </div>
);

const GladeCourseProgress = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get("id");
  const course = String(queryParams.get("course") || "").toUpperCase();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchProgress = useCallback(async () => {
    if (!clientId || !["CC", "DE"].includes(course)) {
      setError("A valid Glade client and course are required");
      setLoading(false);
      return;
    }

    try {
      const response = await getGladeCourseProgress(clientId, course);
      setProgress(response.data.result);
      setError("");
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        "Unable to load Glade course progress";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [clientId, course]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const updateRecord = (recordId, updater) => {
    setProgress((current) => ({
      ...current,
      data: current.data.map((record) =>
        record.id === recordId ? updater(record) : record
      ),
    }));
  };

  const toggleLesson = (recordId, lesson, checked) => {
    updateRecord(recordId, (record) => {
      if (course === "CC" && lesson === 5) {
        const classFiveSubclasses = CLASS_FIVE_SUBCLASSES.reduce(
          (result, subclass) => ({
            ...result,
            [`aud5_${subclass.number}`]: checked ? "checked" : "",
          }),
          { ...(record.classFiveSubclasses || {}) }
        );

        return {
          ...record,
          lessons: { ...record.lessons, [lesson]: checked ? "checked" : "" },
          classFiveSubclasses,
        };
      }

      return {
        ...record,
        lessons: {
          ...record.lessons,
          [lesson]: checked ? "checked" : "",
        },
      };
    });
  };

  const toggleClassFiveSubclass = (recordId, subclassNumber, checked) => {
    updateRecord(recordId, (record) => {
      const classFiveSubclasses = {
        ...(record.classFiveSubclasses || {}),
        [`aud5_${subclassNumber}`]: checked ? "checked" : "",
      };
      const allSubclassesComplete = CLASS_FIVE_SUBCLASSES.every((subclass) =>
        isChecked(classFiveSubclasses[`aud5_${subclass.number}`])
      );

      return {
        ...record,
        lessons: {
          ...record.lessons,
          5: allSubclassesComplete ? "checked" : "",
        },
        classFiveSubclasses,
      };
    });
  };

  const markAllLessons = (recordId) => {
    updateRecord(recordId, (record) => {
      const lessons = {};
      for (let lesson = 1; lesson <= progress.lessonCount; lesson += 1) {
        lessons[lesson] = "checked";
      }

      if (course !== "CC") {
        return { ...record, lessons };
      }

      const classFiveSubclasses = CLASS_FIVE_SUBCLASSES.reduce(
        (result, subclass) => ({
          ...result,
          [`aud5_${subclass.number}`]: "checked",
        }),
        {}
      );

      return {
        ...record,
        lessons,
        classFiveSubclasses,
      };
    });
  };

  const handleUpdate = async () => {
    if (!progress || saving) return;

    setSaving(true);
    try {
      const editableProgress = progress.data.map((record) => ({
        id: record.id,
        lessons: Object.fromEntries(
          Array.from({ length: progress.lessonCount }, (_, index) => {
            const lesson = index + 1;
            return [lesson, record.lessons?.[lesson] || ""];
          })
        ),
        classFiveSubclasses: record.classFiveSubclasses,
      }));
      const response = await updateGladeCourseProgress(
        clientId,
        course,
        editableProgress
      );
      setProgress(response.data.result);
      toast.success(
        response.data?.message || "Course progress updated successfully"
      );
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.error ||
          requestError.response?.data?.message ||
          "Unable to update Glade course progress"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderLessonCheckbox = (record, lesson) => (
    <ProgressCheckbox
      id={`glade-${record.id}-class-${lesson}`}
      checked={isChecked(record.lessons?.[lesson])}
      label={`${record.filerType} filer, class ${lesson}`}
      onChange={(checked) => toggleLesson(record.id, lesson, checked)}
    />
  );

  const renderClassFiveSubclassCheckbox = (record, subclass) => (
    <ProgressCheckbox
      id={`glade-${record.id}-class-5-step-${subclass.number}`}
      checked={isChecked(
        record.classFiveSubclasses?.[`aud5_${subclass.number}`]
      )}
      label={`${record.filerType} filer, class 5 step ${subclass.number}: ${subclass.label}`}
      onChange={(checked) =>
        toggleClassFiveSubclass(record.id, subclass.number, checked)
      }
    />
  );

  const renderFirstCourseRows = () =>
    Array.from(
      { length: FIRST_COURSE_LESSON_COUNT },
      (_, index) => index + 1
    ).map((lesson) => (
      <React.Fragment key={lesson}>
        <tr className={lesson === 5 ? "course-progress-class-five" : ""}>
          <td>
            <div className="course-progress-lesson-label">
              <span className="course-progress-lesson-number">
                {String(lesson).padStart(2, "0")}
              </span>
              {lesson === 5 && (
                <>
                  <span>Class 5</span>
                  <span className="course-progress-count-badge">9 steps</span>
                </>
              )}
            </div>
          </td>
          {progress.data.map((record) => (
            <td key={record.id}>{renderLessonCheckbox(record, lesson)}</td>
          ))}
        </tr>

        {lesson === 5 &&
          CLASS_FIVE_SUBCLASSES.map((subclass) => (
            <tr
              className="course-progress-subclass-row"
              key={`class-five-${subclass.number}`}
            >
              <td>
                <div className="course-progress-subclass-label">
                  <span>{`5.${subclass.number}`}</span>
                  <span>{subclass.label}</span>
                </div>
              </td>
              {progress.data.map((record) => (
                <td key={record.id}>
                  {renderClassFiveSubclassCheckbox(record, subclass)}
                </td>
              ))}
            </tr>
          ))}
      </React.Fragment>
    ));

  const renderSecondCourseRows = () =>
    Array.from({ length: progress.lessonCount }, (_, index) => index + 1).map(
      (lesson) => (
        <tr key={lesson}>
          <td>{String(lesson).padStart(2, "0")}</td>
          {progress.data.map((record) => (
            <td key={record.id}>{renderLessonCheckbox(record, lesson)}</td>
          ))}
        </tr>
      )
    );

  const clientName = progress
    ? `${progress.client.firstName || ""} ${
        progress.client.lastName || ""
      }`.trim()
    : "";

  return (
    <div>
      <div className="wrapper">
        <DashBoardSidebar />
        <div id="content">
          <DashboardNavbar />
          <section className="manage-students course-progress admin-course-progress-page glade-course-progress-page">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center g-3">
                  <div className="col-lg-6">
                    <h4 className="card-title mb-1">
                      {courseNames[course] || "Glade"} Course Progress
                    </h4>
                    {clientName && (
                      <div className="glade-progress-client-title">
                        <span>{clientName}</span>
                        <span className="glade-client-badge">Glade</span>
                      </div>
                    )}
                    {course === "CC" && (
                      <p className="course-progress-structure-note mb-0">
                        14 classes · Class 5 has 9 steps
                      </p>
                    )}
                  </div>
                  {progress && (
                    <div className="col-lg-6 course-progress-header-actions">
                      {progress.data.map((record) => (
                        <button
                          className="btn_brnd"
                          key={record.id}
                          type="button"
                          onClick={() => markAllLessons(record.id)}
                        >
                          Click All
                          {String(record.filerType).toLowerCase() === "joint"
                            ? " (Joint)"
                            : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <ThreeDots
                    height="40"
                    width="80"
                    radius="9"
                    color="#4fa94d"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{ display: "flex", justifyContent: "center" }}
                    wrapperClassName="spinner"
                    visible
                  />
                ) : error || !progress ? (
                  <div className="glade-empty-state">{error}</div>
                ) : (
                  <>
                    <div className="course-progress-table-wrap">
                      <table className="table admin-course-progress-table">
                        <thead>
                          <tr>
                            <th scope="col">Class / Step</th>
                            {progress.data.map((record) => (
                              <th scope="col" key={record.id}>
                                {record.filerType} Filer (
                                {calculatePercentage(
                                  record,
                                  progress.lessonCount
                                )}
                                %)
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {course === "CC"
                            ? renderFirstCourseRows()
                            : renderSecondCourseRows()}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-center">
                      <Link to={`/glade-client-details?id=${clientId}`}>
                        <button
                          className="btn_brnd-outline my-2 me-3"
                          type="button"
                        >
                          Back
                        </button>
                      </Link>
                      <button
                        className="btn_brnd my-3"
                        type="button"
                        onClick={handleUpdate}
                        disabled={saving}
                      >
                        {saving ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GladeCourseProgress;
